// /src/components/SSBHighRiskAlertsReport.jsx
// Detailed, date-range explorer for the "AML: High-Risk Jurisdiction (Source)"
// alerts also delivered via Slack/email (see agents/notification/). Same
// underlying data (idenza.mv_ssb_aml_rule_hits_deduped +
// idenza.ssb_reports_clean), no LLM calls on this path — deterministic
// aggregation only, same as the sibling SSB reports.
import React, { useEffect, useMemo, useState } from "react";
import SSBReportShell from "./SSBReportShell";
import SSBFiltersBar from "./SSBFiltersBar";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  ReferenceLine,
} from "recharts";

const API_BASE =
  process.env.REACT_APP_SSB_AML_API_BASE ||
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    ? "http://localhost:9191/v1/ssb/aml"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app/v1/ssb/aml");

const int0 = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : new Intl.NumberFormat("en-US").format(Number(n));

const short = (s) => (s || "").slice(0, 10);

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const Card = ({ title, value, sub }) => (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #E7ECF3",
      borderRadius: 16,
      padding: 14,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
      minHeight: 88,
    }}
  >
    <div style={{ fontSize: 12, color: "#5E6B7A", marginBottom: 8, fontWeight: 700 }}>{title}</div>
    <div style={{ fontSize: 22, fontWeight: 900, color: "#0B1F33", lineHeight: 1.1 }}>{value}</div>
    {sub ? <div style={{ fontSize: 12, color: "#7A8696", marginTop: 6 }}>{sub}</div> : null}
  </div>
);

export default function SSBHighRiskAlertsReport() {
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());

  const [trend, setTrend] = useState([]);
  const [loadingTrend, setLoadingTrend] = useState(false);
  const [err, setErr] = useState("");

  // Client-side tunable threshold — every program with any hit is always
  // fetched and shown (the backend applies no threshold filter at all);
  // this only controls what counts as "exceeded" for the KPI cards and
  // the By Program chart's highlight color, recomputed instantly with no
  // extra API round-trip. Starts at 1 to match the backend's current
  // DEFAULT_HIT_THRESHOLD, but is free to move from there.
  const [threshold, setThreshold] = useState(1);

  const [programName, setProgramName] = useState("");
  const [breakdown, setBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [breakdownErr, setBreakdownErr] = useState("");

  // "Take a Snapshot" — on-demand Slack delivery of the alert for whatever
  // date range is currently selected, via the same payload builder + Slack
  // agent as the scheduled daily run (so it never drifts from what the
  // dashboard itself shows for that range).
  const [snapshotStatus, setSnapshotStatus] = useState(""); // "" | "sending" | "sent" | "error"
  const [snapshotMsg, setSnapshotMsg] = useState("");

  const takeSnapshot = async () => {
    setSnapshotStatus("sending");
    setSnapshotMsg("");
    try {
      const url = new URL(`${API_BASE}/high-risk-alerts/snapshot`);
      url.searchParams.set("date_from", dateFrom);
      url.searchParams.set("date_to", dateTo);
      const resp = await fetch(url.toString(), { method: "POST" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      setSnapshotStatus("sent");
      setSnapshotMsg(`Sent to Slack — ${json.programs_included} program(s), ${dateFrom} to ${dateTo}`);
    } catch (e) {
      console.error(e);
      setSnapshotStatus("error");
      setSnapshotMsg(`Failed to send: ${e.message || e}`);
    } finally {
      setTimeout(() => setSnapshotStatus(""), 6000);
    }
  };

  // Trend across all programs for the selected date range
  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    let mounted = true;
    (async () => {
      setLoadingTrend(true);
      setErr("");
      try {
        const url = new URL(`${API_BASE}/high-risk-alerts/trend`);
        url.searchParams.set("date_from", dateFrom);
        url.searchParams.set("date_to", dateTo);
        const resp = await fetch(url.toString());
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (!mounted) return;
        setTrend(Array.isArray(json.trend) ? json.trend : []);
      } catch (e) {
        console.error(e);
        if (mounted) setErr(`Failed to load trend: ${e.message || e}`);
      } finally {
        if (mounted) setLoadingTrend(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dateFrom, dateTo]);

  const programs = useMemo(() => {
    const names = new Set(trend.map((r) => r.program_name).filter(Boolean));
    return Array.from(names).sort();
  }, [trend]);

  // Everything below except "By Program" (a deliberately cross-program
  // comparison) scopes to the selected program — "" (All Programs) means
  // no filtering, same as before.
  const filteredTrend = useMemo(() => {
    if (!programName) return trend;
    return trend.filter((r) => r.program_name === programName);
  }, [trend, programName]);

  const summary = useMemo(() => {
    const totalHits = filteredTrend.reduce((s, r) => s + Number(r.hit_count || 0), 0);
    const programsFlagged = new Set(filteredTrend.map((r) => r.program_name)).size;
    const exceeded = filteredTrend.filter((r) => Number(r.hit_count || 0) > threshold);
    const daysExceeded = new Set(exceeded.map((r) => r.date)).size;
    return { totalHits, programsFlagged, exceededCount: exceeded.length, daysExceeded };
  }, [filteredTrend, threshold]);

  // Daily total hits line, plus per-program bar totals for the range
  const dailyTotals = useMemo(() => {
    const map = new Map();
    for (const r of filteredTrend) {
      const cur = map.get(r.date) || { date: short(r.date), hit_count: 0 };
      cur.hit_count += Number(r.hit_count || 0);
      map.set(r.date, cur);
    }
    return Array.from(map.values()).sort((a, b) => (a.date > b.date ? 1 : -1));
  }, [filteredTrend]);

  const programTotals = useMemo(() => {
    const map = new Map();
    for (const r of trend) {
      const cur = map.get(r.program_name) || {
        program_name: r.program_name,
        hit_count: 0,
        exceeded_days: 0,
      };
      cur.hit_count += Number(r.hit_count || 0);
      if (Number(r.hit_count || 0) > threshold) cur.exceeded_days += 1;
      map.set(r.program_name, cur);
    }
    return Array.from(map.values())
      .map((p) => ({ ...p, exceeds_threshold: p.hit_count > threshold }))
      .sort((a, b) => b.hit_count - a.hit_count);
  }, [trend, threshold]);

  // Country breakdown for the selected program
  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    let mounted = true;
    (async () => {
      setLoadingBreakdown(true);
      setBreakdownErr("");
      try {
        const url = new URL(`${API_BASE}/high-risk-alerts/country-breakdown`);
        url.searchParams.set("date_from", dateFrom);
        url.searchParams.set("date_to", dateTo);
        if (programName) url.searchParams.set("program_name", programName);
        const resp = await fetch(url.toString());
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (mounted) setBreakdown(json);
      } catch (e) {
        console.error(e);
        if (mounted) setBreakdownErr(`Failed to load country breakdown: ${e.message || e}`);
        if (mounted) setBreakdown(null);
      } finally {
        if (mounted) setLoadingBreakdown(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [programName, dateFrom, dateTo]);

  return (
    <SSBReportShell
      title="SSB Dashboard — High-Risk Jurisdiction Alerts"
      subtitle={`${dateFrom} to ${dateTo} • ${programs.length} program(s) with hits • Threshold: ${threshold}`}
      rightMeta={[loadingTrend ? "Loading…" : "Live"]}
      leftMeta={["AML: High-Risk Jurisdiction (Source)", "Same data as Slack/email alert"]}
      footerLeft="Idenza • SSB AML Reporting"
      footerRight="Source: mv_ssb_aml_rule_hits_deduped + mv_ssb_high_risk_country_breakdown_daily"
    >
      <SSBFiltersBar columns={4}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>From</div>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>To</div>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>
            Program (country breakdown)
          </div>
          <select
            value={programName}
            onChange={(e) => setProgramName(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            <option value="">All Programs</option>
            {programs.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>
            Threshold (hits)
          </div>
          <input
            type="number"
            min={0}
            step={1}
            value={threshold}
            onChange={(e) => setThreshold(Math.max(0, Number(e.target.value) || 0))}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          />
        </div>
      </SSBFiltersBar>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 12 }}>
        {snapshotMsg ? (
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: snapshotStatus === "error" ? "#B42318" : "#027A48",
            }}
          >
            {snapshotMsg}
          </div>
        ) : null}
        <button
          onClick={takeSnapshot}
          disabled={snapshotStatus === "sending"}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            background:
              snapshotStatus === "sending"
                ? "#94A3B8"
                : "linear-gradient(90deg, #0B3A6E 0%, #1F63A8 100%)",
            color: "#fff",
            fontWeight: 800,
            fontSize: 13,
            cursor: snapshotStatus === "sending" ? "default" : "pointer",
            boxShadow: "0 10px 20px rgba(11,58,110,0.18)",
          }}
        >
          {snapshotStatus === "sending" ? "Sending…" : "📸 Take Snapshot → Slack"}
        </button>
      </div>

      {err ? (
        <div
          style={{
            background: "#FEF3F2",
            border: "1px solid #FECACA",
            color: "#7F1D1D",
            padding: 12,
            borderRadius: 16,
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        <Card
          title="Total Hits"
          value={int0(summary.totalHits)}
          sub={programName ? `${programName} only` : "All programs — distinct account-day hits"}
        />
        <Card title="Programs With Hits" value={int0(summary.programsFlagged)} sub="Always shown, no threshold applied" />
        <Card
          title="Program-Days Above Threshold"
          value={int0(summary.exceededCount)}
          sub={`> ${threshold} hit(s)/day`}
        />
        <Card title="Days With ≥1 Exceedance" value={int0(summary.daysExceeded)} sub={`> ${threshold} hit(s)/day`} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 12, marginBottom: 12 }}>
        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E7ECF3",
            borderRadius: 16,
            padding: 12,
            boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          }}
        >
          <div style={{ fontWeight: 900, color: "#0B1F33", marginBottom: 8 }}>
            Daily Hit Count — {programName || "all programs"}
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyTotals} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" style={{ fontSize: 11 }} />
                <YAxis style={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="hit_count" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E7ECF3",
            borderRadius: 16,
            padding: 12,
            boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <div style={{ fontWeight: 900, color: "#0B1F33" }}>By Program (range total)</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>
              Red = exceeds threshold ({threshold}){programName ? " • outlined = selected" : ""}
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={programTotals}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} style={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="program_name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip />
                <ReferenceLine
                  x={threshold}
                  stroke="#EF4444"
                  strokeDasharray="4 4"
                  label={{ value: `Threshold: ${threshold}`, position: "top", fontSize: 11, fill: "#EF4444" }}
                />
                <Bar dataKey="hit_count">
                  {programTotals.map((p) => (
                    <Cell
                      key={p.program_name}
                      fill={p.exceeds_threshold ? "#EF4444" : "#1F63A8"}
                      stroke={p.program_name === programName ? "#0B1F33" : "none"}
                      strokeWidth={p.program_name === programName ? 2 : 0}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E7ECF3",
          borderRadius: 16,
          padding: 12,
          boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <div style={{ fontWeight: 900, color: "#0B1F33" }}>
            Country Breakdown — {programName || "All Programs"}
          </div>
          <div style={{ fontSize: 12, color: "#64748B" }}>
            {loadingBreakdown ? "Loading…" : "Flagged account-days, same-day foreign transactions"}
          </div>
        </div>

        {breakdownErr ? (
          <div
            style={{
              background: "#FEF3F2",
              border: "1px solid #FECACA",
              color: "#7F1D1D",
              padding: 12,
              borderRadius: 12,
              marginBottom: 12,
            }}
          >
            {breakdownErr}
          </div>
        ) : null}

        <div style={{ overflowX: "auto", border: "1px solid #F1F5F9", borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #E7ECF3", background: "#F8FAFC" }}>
                {(breakdown?.columns || ["Country", "FATF Status", "Transactions", "% of Txns", "Volume (USD)", "% of Volume"]).map(
                  (c) => (
                    <th key={c} style={{ padding: 10 }}>
                      {c}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {(breakdown?.rows || []).map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: 10, fontWeight: ci === 0 ? 900 : 400, color: ci === 0 ? "#0B1F33" : "inherit" }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
              {!loadingBreakdown && (!breakdown?.rows || breakdown.rows.length === 0) ? (
                <tr>
                  <td colSpan={6} style={{ padding: 12, color: "#64748B" }}>
                    No foreign-country transactions found for this program/date range.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 10, fontSize: 12, color: "#64748B" }}>
          Directionally reliable, not penny-precise — an account can touch more than one foreign country on the
          same flagged day. FATF status reflects this rule's own High-Risk Jurisdiction list, not a live FATF feed.
        </div>
      </div>
    </SSBReportShell>
  );
}
